import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, themes, defaultTheme } from './themes';

interface ThemeContextType {
  theme: Theme;
  setTheme: (themeName: string) => void;
  availableThemes: Record<string, Theme>;
  updateCustomThemeColor: (colorKey: string, colorValue: string) => void;
  updateCustomThemeTypography: (typographyKey: string, typographyValue: string) => void;
  resetCustomTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'military-center-theme';
const CUSTOM_THEME_STORAGE_KEY = 'military-center-custom-theme';
const CUSTOM_TYPOGRAPHY_STORAGE_KEY = 'military-center-custom-typography';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    const savedCustomTheme = localStorage.getItem(CUSTOM_THEME_STORAGE_KEY);
    const savedCustomTypography = localStorage.getItem(CUSTOM_TYPOGRAPHY_STORAGE_KEY);
    
    // Load custom theme colors if they exist
    if (savedCustomTheme) {
      try {
        const customColors = JSON.parse(savedCustomTheme);
        themes.custom.colors = customColors;
      } catch (e) {
        console.error('Failed to parse custom theme:', e);
      }
    }
    
    // Load custom typography if it exists
    if (savedCustomTypography) {
      try {
        const customTypography = JSON.parse(savedCustomTypography);
        themes.custom.typography = customTypography;
      } catch (e) {
        console.error('Failed to parse custom typography:', e);
      }
    }
    
    return savedTheme && themes[savedTheme] ? themes[savedTheme] : defaultTheme;
  });

  const setTheme = (themeName: string) => {
    if (themes[themeName]) {
      setThemeState(themes[themeName]);
      localStorage.setItem(THEME_STORAGE_KEY, themeName);
    }
  };

  const updateCustomThemeColor = (colorKey: string, colorValue: string) => {
    const updatedColors = {
      ...themes.custom.colors,
      [colorKey]: colorValue,
    };
    
    themes.custom.colors = updatedColors;
    localStorage.setItem(CUSTOM_THEME_STORAGE_KEY, JSON.stringify(updatedColors));
    
    // If currently using custom theme, update the state
    if (theme.name === 'custom') {
      setThemeState({ ...themes.custom, colors: updatedColors, typography: themes.custom.typography });
    }
  };

  const updateCustomThemeTypography = (typographyKey: string, typographyValue: string) => {
    const updatedTypography = {
      ...themes.custom.typography,
      [typographyKey]: typographyValue,
    };
    
    themes.custom.typography = updatedTypography;
    localStorage.setItem(CUSTOM_TYPOGRAPHY_STORAGE_KEY, JSON.stringify(updatedTypography));
    
    // If currently using custom theme, update the state
    if (theme.name === 'custom') {
      setThemeState({ ...themes.custom, colors: themes.custom.colors, typography: updatedTypography });
    }
  };

  const resetCustomTheme = () => {
    const darkColors = themes.dark.colors;
    const darkTypography = themes.dark.typography;
    themes.custom.colors = { ...darkColors };
    themes.custom.typography = { ...darkTypography };
    localStorage.setItem(CUSTOM_THEME_STORAGE_KEY, JSON.stringify(darkColors));
    localStorage.setItem(CUSTOM_TYPOGRAPHY_STORAGE_KEY, JSON.stringify(darkTypography));
    
    // If currently using custom theme, update the state
    if (theme.name === 'custom') {
      setThemeState({ ...themes.custom, colors: darkColors, typography: darkTypography });
    }
  };

  useEffect(() => {
    // Apply theme CSS variables to root
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    Object.entries(theme.typography).forEach(([key, value]) => {
      root.style.setProperty(`--typography-${key}`, value);
    });
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, availableThemes: themes, updateCustomThemeColor, updateCustomThemeTypography, resetCustomTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
